import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { useClubPlanPrices } from '@/hooks/useClubPlanPrices';
import { formatCurrency } from '@/lib/constants';
import { Check, Crown, Zap, Loader2, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PlanUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PlanData {
  id: string;
  name: string;
  icon: typeof Zap;
  price: string;
  description: string;
  features: string[];
  inheritLabel?: string;
  tier: string;
}

// Catálogo enxuto (20260703150000_remove_pro_tier.sql): só free e
// enterprise — as features do ex-Pro foram absorvidas pelo Enterprise.
//
// SÓ MOSTRA, não troca (11/10/2026). Upgrade, downgrade e cancelamento passam a
// ser feitos pelo ADMIN da PPS (RPC `admin_alterar_plano_do_clube`, tela de
// Clubes do admin): não existe mais checkout — `club-upgrade-checkout` e
// `club-downgrade` eram Stripe e viraram stub 410. A cobrança do plano segue
// pelas agendas da PPS, no Asaas. O diálogo continua aqui porque o clube
// precisa ver o que cada plano inclui e saber com quem falar.
export function PlanUpgradeDialog({ open, onOpenChange }: PlanUpgradeDialogProps) {
  const { t } = useTranslation();
  const { tier } = usePlanFeatures();

  const buildStaticPlans = (): PlanData[] => [
    {
      id: 'free',
      tier: 'free',
      name: 'Free',
      icon: Zap,
      price: t('common.free'),
      description: '',
      features: [
        'CRM · ' + t('planUpgrade.upTo50Leads', 'até 50 leads'),
        t('nav.agenda'),
        t('nav.courts'),
        t('nav.categories'),
        t('nav.tournaments'),
      ],
    },
    {
      id: 'enterprise',
      tier: 'enterprise',
      name: 'Enterprise',
      icon: Crown,
      price: '',
      description: '',
      inheritLabel: t('planUpgrade.everythingInFree', 'Tudo do Free +'),
      features: [
        t('planUpgrade.unlimitedCRM', 'CRM ilimitado'),
        t('nav.opportunities'),
        t('planUpgrade.academy', 'Academia'),
        t('planUpgrade.plansAndInvoices', 'Planos e Faturas'),
        t('nav.commissions'),
        t('planUpgrade.paymentMethods', 'Métodos de Pagamento'),
        t('nav.expenses'),
        'NFS-e',
        t('nav.financialPanel'),
        'WhatsApp e Instagram',
        t('planUpgrade.integrations', 'Integrações (Wellhub, TotalPass)'),
        t('planUpgrade.apiAndSite', 'API e Site Público'),
        t('planUpgrade.prioritySupport', 'Suporte prioritário'),
      ],
    },
  ];

  const { data: precos, isLoading: fetching } = useClubPlanPrices(open);

  // Preço do banco por cima do catálogo estático; sem linha no banco (ou com a
  // query falhando), o catálogo estático sozinho.
  const plans: PlanData[] = buildStaticPlans().map((sp) => {
    const dbRow = (precos ?? []).find((r) => r.plan_tier === sp.tier);
    if (!dbRow) return sp;
    return {
      ...sp,
      id: dbRow.id,
      name: dbRow.name || sp.name,
      price: dbRow.price_monthly > 0
        ? `${formatCurrency(Number(dbRow.price_monthly))}${t('planUpgrade.porMes')}`
        : sp.price,
      description: dbRow.description || sp.description,
    };
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl">{t('planUpgrade.title')}</DialogTitle>
          <DialogDescription>{t('planUpgrade.mudarComPps.description')}</DialogDescription>
        </DialogHeader>
        {fetching ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {plans.map(plan => {
              const isCurrent = plan.tier === tier;
              const Icon = plan.icon;
              return (
                <div
                  key={plan.tier}
                  className={`border rounded-xl p-5 flex flex-col gap-4 transition-all ${
                    isCurrent
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-base">{plan.name}</span>
                    {isCurrent && (
                      <Badge variant="secondary" className="text-[10px] ml-auto">{t('planUpgrade.currentPlan')}</Badge>
                    )}
                  </div>
                  {plan.price && <div className="text-2xl font-bold">{plan.price}</div>}
                  {plan.description && (
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  )}
                  <ul className="space-y-2 text-sm flex-1">
                    {plan.inheritLabel && (
                      <li className="font-medium text-primary mb-1">{plan.inheritLabel}</li>
                    )}
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-dashed p-3 text-sm">
          <MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <div className="space-y-0.5">
            <p className="font-medium">{t('planUpgrade.mudarComPps.title')}</p>
            <p className="text-muted-foreground">{t('planUpgrade.mudarComPps.hint')}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
